import { useState } from 'react';
import { Button, Card, Field, Notice, TextInput } from '../../shell/kit';
import { errorText } from '../../shell/errors';
import { useToast } from '../../shell/toast';
import {
  changeOwnPassword,
  confirmOwnTotp,
  enrollOwnTotp,
  type TotpEnrollment,
} from './api';
import type { StaffTranslate } from './i18n';

/**
 * Свой профиль.
 *
 * Живёт в разделе сотрудников, но правами `employee.*` не гейтится: менять
 * свой пароль обязан любой, включая кассира, у которого этих прав нет и быть
 * не должно.
 *
 * Секрет второго фактора выдаётся ровно тому, кто его заводит. У администратора
 * есть только сброс — иначе второй фактор перестал бы быть вторым: администратор
 * мог бы генерировать чужие коды.
 */

export interface ProfileTabProps {
  readonly t: StaffTranslate;
  readonly mustChangePassword: boolean;
  readonly totpConfirmed: boolean;
  readonly onPasswordChanged: () => void;
}

export function ProfileTab(props: ProfileTabProps) {
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);

  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null);
  const [code, setCode] = useState('');
  const [confirmed, setConfirmed] = useState(props.totpConfirmed);

  function fail(err: unknown): void {
    const shown = errorText(err);
    toast.error(shown.message, shown.code);
  }

  async function submitPassword(): Promise<void> {
    if (!current || !next) return;
    if (next !== repeat) {
      toast.warn(props.t('staff.profile.mismatch'));
      return;
    }
    setBusy(true);
    try {
      await changeOwnPassword(current, next);
      setCurrent('');
      setNext('');
      setRepeat('');
      toast.ok(props.t('staff.profile.changed'));
      props.onPasswordChanged();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  async function startTotp(): Promise<void> {
    setBusy(true);
    try {
      setEnrollment(await enrollOwnTotp());
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  async function submitTotp(): Promise<void> {
    setBusy(true);
    try {
      await confirmOwnTotp(code.trim());
      setEnrollment(null);
      setCode('');
      setConfirmed(true);
      toast.ok(props.t('staff.profile.totpDone'));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {props.mustChangePassword ? (
        <Notice tone="warn" title={props.t('staff.profile.password')}>
          {props.t('staff.profile.mustChange')}
        </Notice>
      ) : null}

      <Card title={props.t('staff.profile.password')}>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label={props.t('staff.profile.current')}>
            <TextInput
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </Field>
          <Field label={props.t('staff.profile.new')} hint={props.t('staff.form.passwordHint')}>
            <TextInput
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          </Field>
          <Field label={props.t('staff.profile.repeat')}>
            <TextInput
              type="password"
              autoComplete="new-password"
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
            />
          </Field>
        </div>
        <div className="mt-4">
          <Button tone="primary" disabled={busy} onClick={() => void submitPassword()}>
            {props.t('staff.profile.password')}
          </Button>
        </div>
      </Card>

      <Card title={props.t('staff.profile.totp')}>
        <p className="text-sm text-ink-muted">
          {confirmed ? props.t('staff.profile.totpOn') : props.t('staff.profile.totpOff')}
        </p>

        {!enrollment ? (
          <div className="mt-4">
            <Button disabled={busy} onClick={() => void startTotp()}>
              {props.t('staff.profile.totpStart')}
            </Button>
          </div>
        ) : (
          <div className="mt-4 flex flex-col gap-4">
            {/*
              * Секрет и ссылка `otpauth://` — текстом. Картинки с кодом здесь
              * нет намеренно: рисовать её пришлось бы собственным кодировщиком,
              * а неверная маска даёт матрицу, которая выглядит как QR и не
              * сканируется ничем. Пока сканирование не проверено на живых
              * приложениях, честнее дать то, что заведомо работает руками.
              */}
            <Notice tone="warn" title={props.t('staff.profile.totpSecret')}>
              <code className="mt-1 block font-mono text-sm break-all">{enrollment.secret}</code>
              <p className="mt-2 text-sm">{props.t('staff.profile.totpManual')}</p>
              <code className="mt-2 block font-mono text-xs break-all opacity-70">
                {enrollment.uri}
              </code>
              <Button
                size="sm"
                className="mt-2"
                onClick={() => {
                  void navigator.clipboard
                    ?.writeText(enrollment.secret)
                    .then(() => toast.ok(props.t('staff.profile.copied')));
                }}
              >
                {props.t('staff.profile.copy')}
              </Button>
            </Notice>

            <Field label={props.t('staff.profile.totpCode')}>
              <TextInput
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </Field>
            <div>
              <Button
                tone="primary"
                disabled={busy || code.trim().length !== 6}
                onClick={() => void submitTotp()}
              >
                {props.t('staff.profile.totpConfirm')}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </>
  );
}
